import AsyncStorage from '@react-native-async-storage/async-storage'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

/** Scroll fraction that counts as "read", per PP-4819. */
export const COMPLETION_THRESHOLD = 0.7

type ArticleProgress = {
  /** Furthest scroll fraction reached, 0–1. */
  maxScroll: number
  /** Set once maxScroll first crosses COMPLETION_THRESHOLD. */
  completedAt?: number
  lastOpenedAt: number
}

type ReadProgressState = {
  byArticleId: Record<string, ArticleProgress>
}

type ReadProgressActions = {
  markOpened: (articleId: string) => void
  /**
   * Records how far down the article the reader got.
   * Returns true the first time an article crosses the threshold, which is the
   * moment to award diamonds/badges — the caller fires the reward so this store
   * stays a plain record of progress.
   */
  recordScroll: (articleId: string, scroll: number) => boolean
  isCompleted: (articleId: string) => boolean
  progressFor: (articleId: string) => number
}

export const useReadProgressStore = create<ReadProgressState & ReadProgressActions>()(
  persist(
    (set, get) => ({
      byArticleId: {},

      markOpened: (articleId) =>
        set((state) => ({
          byArticleId: {
            ...state.byArticleId,
            [articleId]: {
              maxScroll: state.byArticleId[articleId]?.maxScroll ?? 0,
              completedAt: state.byArticleId[articleId]?.completedAt,
              lastOpenedAt: Date.now()
            }
          }
        })),

      recordScroll: (articleId, scroll) => {
        const previous = get().byArticleId[articleId]
        const maxScroll = Math.max(previous?.maxScroll ?? 0, Math.min(scroll, 1))

        if (previous && maxScroll <= previous.maxScroll) {
          return false
        }

        const justCompleted = !previous?.completedAt && maxScroll >= COMPLETION_THRESHOLD

        set((state) => ({
          byArticleId: {
            ...state.byArticleId,
            [articleId]: {
              maxScroll,
              completedAt: previous?.completedAt ?? (justCompleted ? Date.now() : undefined),
              lastOpenedAt: previous?.lastOpenedAt ?? Date.now()
            }
          }
        }))

        return justCompleted
      },

      isCompleted: (articleId) => Boolean(get().byArticleId[articleId]?.completedAt),
      progressFor: (articleId) => get().byArticleId[articleId]?.maxScroll ?? 0
    }),
    {
      name: 'read-progress-storage',
      storage: createJSONStorage(() => AsyncStorage)
    }
  )
)
